import { cn } from "@components/ui/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import type { TItem, TTitleValueItem } from "./types"
import { Skeleton } from "@components/ui/skeleton"

const layoutVariants = cva("", {
  variants: {
    variant: {
      gridCards: "tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3",
      gridClean: "tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3",
      custom: "",
    },
  },
  defaultVariants: {
    variant: "custom",
  },
})

const wrapperVariants = cva("", {
  variants: {
    variant: {
      gridCards: "tw:flex tw:flex-col tw:gap-2 tw:rounded-md tw:p-2 tw:shadow",
      gridClean: "tw:flex tw:gap-2",
      custom: "",
    },
  },
  defaultVariants: {
    variant: "custom",
  },
})

const titleVariants = cva("", {
  variants: {
    variant: {
      gridCards: "tw:text-sm tw:text-gray-500",
      gridClean: "tw:text-sm tw:text-gray-500",
      custom: "",
    },
  },
  defaultVariants: {
    variant: "custom",
  },
})

const valueVariants = cva("", {
  variants: {
    variant: {
      gridCards: "tw:font-medium",
      gridClean: "tw:font-medium",
      custom: "",
    },
  },
  defaultVariants: {
    variant: "custom",
  },
})

function TitleValueList({
  items,
  variant,
  variantClasses,
}: {
  items: TTitleValueItem[]
  variantClasses?: {
    layout?: string
    wrapper?: string
    title?: string
    value?: string
  }
} & VariantProps<typeof layoutVariants>) {
  const renderItem = (item: TItem) => {
    const { content, isPending } = item
    if (isPending) return <Skeleton className="tw:h-5 tw:rounded" />

    return content ?? ""
  }

  return (
    <div className={cn(layoutVariants({ variant }), variantClasses?.layout)}>
      {items.map((item, index) => (
        <div
          key={`title-value-item-${index}`}
          className={cn(
            wrapperVariants({ variant }),
            variantClasses?.wrapper,
            item.className,
          )}
        >
          {item.title && (
            <div
              className={cn(
                titleVariants({ variant }),
                variantClasses?.title,
                item.title.isPending && "tw:flex-grow",
                item.title.className,
              )}
            >
              {renderItem(item.title)}
            </div>
          )}
          {item.value && (
            <div
              className={cn(
                valueVariants({ variant }),
                variantClasses?.value,
                item.value.isPending && "tw:flex-grow",
                item.value.className,
              )}
            >
              {renderItem(item.value)}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export {
  TitleValueList,
  layoutVariants,
  wrapperVariants,
  titleVariants,
  valueVariants,
}
